import { get } from 'lodash-es'
import jsonApiNormalize from '@base/json-api-normalizer.js'
import PhotoWebAPI from '@api/photo_web_api.js'
import { receivePhoto } from '@actions/photo_actions.js'

const POLL_CAP = 10
const POLL_TIMEOUT_BASE = 1200

class PhotoPollQueue {
  constructor() {
    this.runningPolls = {}
  }

  startPollingIfNeeded(photo, dispatch) {
    if (typeof dispatch !== 'function') {
      throw new Error('Expected dispatch to be a function')
    }
    const id = get(photo, ['id'])
    if (this.runningPolls[id]) return // already polling
    // see if we are still processing
    // Note: We want to be careful here and only start polling if the image_processing_complete is explicitly false.
    // If it is undefined, we don't want to start polling because the image_processing_complete property may not exist, or it might be true
    const stillProcessing =
      get(photo, ['image_processing_complete']) === false ||
      get(photo, ['attributes', 'image_processing_complete']) === false
    if (stillProcessing) {
      this.runningPolls[id] = {
        id,
        count: 0,
        errorCount: 0,
        dispatch,
      }
      // if photo was in rest style, assume we want api version 1. If has 'attributes' property, assume json-api, aka api version 2
      const apiVersion = photo.hasOwnProperty('attributes') ? 2 : 1
      setTimeout(this._poll.bind(this, id, apiVersion), POLL_TIMEOUT_BASE)
    }
  }

  _poll(id, apiVersion = 1) {
    PhotoWebAPI.fetchPhoto(id, apiVersion).then(
      res => {
        const parsedJSON = res.data
        this.runningPolls[id].count++

        let photo
        if (apiVersion === 2) {
          // response was jsonapi style, normalize it
          const normalized = jsonApiNormalize(parsedJSON, { camelizeKeys: false, camelizeTypeValues: false })
          photo = normalized.photos[id]
        } else {
          // response was rest style, use it directly
          photo = parsedJSON
        }
        const { count } = this.runningPolls[id]
        const stillProcessing =
          get(photo, ['image_processing_complete']) === false ||
          get(photo, ['attributes', 'image_processing_complete']) === false
        if (!stillProcessing) {
          // done processing, receive the photo and remove the poll
          this.runningPolls[id].dispatch(receivePhoto(photo))
          this._removePoll(id)
        } else {
          // still processing, keep polling
          const timeout = POLL_TIMEOUT_BASE * count
          if (this._reachedPollCap(id)) {
            this._removePoll(id)
          } else {
            setTimeout(this._poll.bind(this, id, apiVersion), timeout)
          }
        }
      },
      err => {
        this.runningPolls[id].errorCount++
        const { errorCount, count } = this.runningPolls[id]
        if (errorCount >= 1) return
        if (this._reachedPollCap(id)) {
          this._removePoll(id)
        } else {
          const timeout = POLL_TIMEOUT_BASE * count
          setTimeout(this._poll.bind(this, id, apiVersion), timeout)
        }
      }
    )
  }

  _removePoll(id) {
    delete this.runningPolls[id]
  }

  _reachedPollCap(id) {
    return this.runningPolls[id].count >= POLL_CAP
  }
}

export default new PhotoPollQueue()
